import type { LoaderFunctionArgs } from "react-router-dom";
import { redirect } from "react-router-dom";
import { organizationAPI, billingAPI, APIError } from "../../lib/api";
import type { OrganizationResponse, BillingStatus, InvoiceResponse } from "../../types/api";

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isValidUUID(value: string): boolean {
  return UUID_REGEX.test(value);
}

export interface BillingData {
  organization: OrganizationResponse;
  billing: BillingStatus;
  invoices: InvoiceResponse[];
}

export async function billingLoader({ params }: LoaderFunctionArgs): Promise<BillingData> {
  const { id } = params;

  if (!id || !isValidUUID(id)) {
    throw redirect("/organizations");
  }

  try {
    const [orgDetail, billingResponse] = await Promise.all([
      organizationAPI.getById(id),
      billingAPI.getStatus(id),
    ]);

    let invoices: InvoiceResponse[] = [];
    if (billingResponse.billing.stripeConfigured && billingResponse.billing.tier !== "free") {
      try {
        const invoicesResponse = await billingAPI.getInvoices(id);
        invoices = invoicesResponse.invoices;
      } catch {
        // Invoices are optional, continue without them
      }
    }

    return {
      organization: orgDetail.organization,
      billing: billingResponse.billing,
      invoices,
    };
  } catch (error) {
    if (error instanceof APIError && error.statusCode === 404) {
      throw redirect("/organizations");
    }
    if (error instanceof APIError && error.statusCode === 403) {
      throw redirect("/organizations");
    }
    throw error;
  }
}
